import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Card from '../components/common/Card'
import TootiDialog from '../components/common/Dialog'
import Spinner from '../components/common/Spinner'
import Switch from '../components/common/Switch'
import { cancelAutopayDialog, enableAutopayDialog } from '../lib/alerts'
import { BANKS, CEILINGS, MAX_DAY, amount, bankById, ceilingToMinor, ibanComplete, ibanTail, refusalFor, toStatus } from '../lib/autopay'
import { faDigits } from '../lib/numerals'
import { autopayStatus, cancelMandate, createMandate } from '../services/autopay'

function StepLabel({ children }) {
  return <p className="text-xs font-semibold text-ink-3">{children}</p>
}

function Helper({ children }) {
  return <p className="text-[11px] leading-[1.85] text-ink-3">{children}</p>
}

function CheckGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function Explainer() {
  const { t } = useTranslation('autopay')
  return (
    <div className="space-y-3 rounded-2xl bg-linear-to-br from-hero-from to-hero-to p-[18px] text-white">
      <p className="text-[11.5px] font-bold text-white/75">{t('explainer.kicker')}</p>
      <h2 className="text-lg font-bold leading-[1.5]">{t('explainer.heading')}</h2>
      <p className="text-[12.5px] leading-[1.9] text-white/85">
        {t('explainer.body')}
      </p>
    </div>
  )
}

function Fact({ label, value }) {
  return (
    <div className="flex-1 space-y-1 rounded-[13px] bg-surface-2 p-3">
      <p className="text-[10.5px] text-ink-3">{label}</p>
      <p className="text-sm font-bold text-ink">{value}</p>
    </div>
  )
}

function ActiveCard({ status }) {
  const { t } = useTranslation('autopay')
  const bank = bankById(status.bank)
  return (
    <div className="space-y-3.5 rounded-[20px] border-[1.5px] border-good bg-surface p-[18px]">
      <div className="flex items-center gap-2.5">
        <span className="grid size-[38px] shrink-0 place-items-center rounded-[11px] bg-good-tint text-good">
          <CheckGlyph />
        </span>
        <div className="space-y-0.5">
          <p className="text-sm font-bold text-good-ink">{t('activeCard.active')}</p>
          <p className="text-[11.5px] text-ink-2">
            {t('activeCard.bankLine', { bankFull: bank.full, tail: faDigits(status.ibanTail) })}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Fact label={t('activeCard.monthlyAmountLabel')} value={amount()} />
        <Fact label={t('activeCard.nextWithdrawalLabel')} value={t('activeCard.nextWithdrawalValue', { day: faDigits(status.day) })} />
      </div>
      <div className="flex gap-2">
        <Fact label={t('activeCard.monthlyCeilingLabel')} value={t('page.thousandSuffix', { count: faDigits(status.ceiling) })} />
        <Fact label={t('activeCard.reminderLabel')} value={status.remind ? t('activeCard.twoDaysBefore') : t('activeCard.off')} />
      </div>
    </div>
  )
}

/** Always the honest empty state: there is no withdrawal scheduler yet (B8's own scope note). */
function History() {
  const { t } = useTranslation('autopay')
  return (
    <div className="space-y-2.5">
      <StepLabel>{t('history.title')}</StepLabel>
      <Helper>{t('history.empty')}</Helper>
    </div>
  )
}

function DaySteppper({ day, onChange }) {
  const { t } = useTranslation('autopay')
  return (
    <div className="flex items-center justify-between">
      <span className="text-[13.5px] font-semibold text-ink">{t('daySteppper.label')}</span>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => onChange(day === MAX_DAY ? 1 : day + 1)}
          className="grid size-8 place-items-center rounded-full bg-surface-2 text-ink hover:bg-surface-3"
          aria-label={t('daySteppper.later')}
        >
          +
        </button>
        <span className="min-w-16 text-center text-sm font-bold tnum text-ink">{t('daySteppper.value', { day: faDigits(day) })}</span>
        <button
          type="button"
          onClick={() => onChange(day === 1 ? MAX_DAY : day - 1)}
          className="grid size-8 place-items-center rounded-full bg-surface-2 text-ink hover:bg-surface-3"
          aria-label={t('daySteppper.earlier')}
        >
          −
        </button>
      </div>
    </div>
  )
}

/**
 * Direct debit (design page 17, `autopay`; P-autopay of the go-live plan),
 * two states: the mandate form and the active card. The mandate is real on
 * the server (B8), but only a stub gateway is behind it — no bank is
 * actually contacted — so activating/cancelling is a real, possibly-
 * failing request, not a local write. Only the last four digits of the
 * IBAN are ever sent.
 */
export default function Autopay() {
  const { t } = useTranslation('autopay')
  const [status, setStatus] = useState(null)
  const [bank, setBank] = useState('melli')
  const [iban, setIban] = useState('')
  const [ceiling, setCeiling] = useState(200)
  const [day, setDay] = useState(5)
  const [remind, setRemind] = useState(true)
  const [askEnable, setAskEnable] = useState(false)
  const [askCancel, setAskCancel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false
    autopayStatus()
      .then((data) => (cancelled ? null : toStatus(data)))
      // A read failure still needs the form fields set to something: the
      // learner can retry the write itself, which surfaces its own error.
      .catch(() => (cancelled ? null : toStatus(null)))
      .then((parsed) => {
        if (!parsed) return
        setStatus(parsed)
        if (!parsed.active) {
          setBank(parsed.bank)
          setCeiling(parsed.ceiling)
          setDay(parsed.day)
          setRemind(parsed.remind)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const ibanOk = ibanComplete(iban)

  async function activate() {
    setAskEnable(false)
    if (busy) return
    setBusy(true)
    setMessage(null)
    try {
      const data = await createMandate({ bank, ibanTail: ibanTail(iban), monthlyCapMinor: ceilingToMinor(ceiling), withdrawalDay: day, remindEnabled: remind })
      setStatus(toStatus(data))
      setMessage({ good: true, text: t('page.activateSuccess') })
    } catch (e) {
      setMessage(refusalFor(e))
    } finally {
      setBusy(false)
    }
  }

  async function cancel() {
    setAskCancel(false)
    if (busy) return
    setBusy(true)
    setMessage(null)
    try {
      const data = await cancelMandate()
      setStatus(toStatus(data))
      setMessage({ good: true, text: t('page.cancelSuccess') })
    } catch (e) {
      setMessage(refusalFor(e))
    } finally {
      setBusy(false)
    }
  }

  // Waited for on purpose, unlike the paywall: an already-active mandate
  // must not flash the setup form for a frame before flipping to the
  // active card.
  if (status === null) return <Spinner label={t('page.checkingStatus')} />

  return (
    <div className="mx-auto max-w-md space-y-4" aria-busy={busy || undefined}>
      <header className="flex items-center gap-2">
        <Link to="/profile" aria-label={t('page.backToProfile')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
      </header>

      {status?.active ? (
        <div className="space-y-3.5">
          <ActiveCard status={status} />
          <History />
          <button
            type="button"
            onClick={() => setAskCancel(true)}
            disabled={busy}
            className="h-12 w-full rounded-xl bg-bad-tint text-[13.5px] font-bold text-bad-ink disabled:cursor-wait"
          >
            {t('page.cancelButton')}
          </button>
        </div>
      ) : (
        <div className="space-y-3.5">
          <Explainer />

          <StepLabel>{t('page.step1Bank')}</StepLabel>
          <div className="flex flex-wrap gap-1.5">
            {BANKS.map(({ id, chip }) => (
              <button
                key={id}
                type="button"
                aria-pressed={bank === id}
                onClick={() => setBank(id)}
                className={`h-10 rounded-xl px-3.5 text-[12.5px] font-semibold transition ${
                  bank === id ? 'bg-brand-600 text-white dark:text-slate-900' : 'border border-line bg-surface text-ink-2'
                }`}
              >
                {chip}
              </button>
            ))}
          </div>

          <StepLabel>{t('page.step2Iban')}</StepLabel>
          <div dir="ltr" className="flex h-14 items-center gap-2.5 rounded-2xl border-[1.5px] border-line bg-surface px-3.5">
            <span className="text-[13px] font-bold text-ink-3">IR</span>
            <input
              value={iban}
              onChange={(e) => setIban(e.target.value.replace(/[^\d ]/g, '').slice(0, 40))}
              inputMode="numeric"
              placeholder="12 0120 0000 0000 1234 5678 90"
              className="w-full bg-transparent text-sm font-semibold text-ink placeholder:text-ink-3 focus:outline-none"
            />
          </div>
          <Helper>{t('page.ibanHelper')}</Helper>

          <StepLabel>{t('page.step3Ceiling')}</StepLabel>
          <div className="flex gap-1.5">
            {CEILINGS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={ceiling === value}
                onClick={() => setCeiling(value)}
                className={`h-11 flex-1 rounded-xl text-[12.5px] font-bold transition ${
                  ceiling === value ? 'bg-brand-600 text-white dark:text-slate-900' : 'border-[1.5px] border-line bg-surface text-ink-2'
                }`}
              >
                {t('page.thousandSuffix', { count: faDigits(value) })}
              </button>
            ))}
          </div>
          <Helper>{t('page.ceilingHelper', { amount: amount() })}</Helper>

          <Card as="div" pad="p-4" className="space-y-3.5">
            <DaySteppper day={day} onChange={setDay} />
            <div className="h-px bg-line" />
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <p className="text-[13.5px] font-semibold text-ink">{t('page.reminderHeading')}</p>
                <p className="text-[11px] text-ink-3">{t('page.reminderSub')}</p>
              </div>
              <Switch checked={remind} onToggle={() => setRemind((r) => !r)} label={t('page.reminderHeading')} />
            </div>
          </Card>

          <div className="space-y-2 rounded-2xl bg-surface-2 p-4">
            <p className="text-xs font-bold text-ink">{t('page.summaryTitle')}</p>
            <p className="text-[11.5px] leading-[1.9] text-ink-2">
              {t('page.summaryText', { day: faDigits(day), amount: amount(), bankFull: bankById(bank).full, ceiling: faDigits(ceiling) })}
            </p>
          </div>

          {/* Looks inert until the IBAN validates, but a tap always responds —
              early, it says what's missing rather than doing nothing, same
              as the Android screen — so this is a plain button, not the
              shared disabled-aware Button. */}
          <button
            type="button"
            aria-busy={busy || undefined}
            onClick={() => (ibanOk ? setAskEnable(true) : setMessage({ good: false, text: t('page.ibanIncomplete') }))}
            className={`flex h-[52px] w-full items-center justify-center gap-2 rounded-xl text-[15px] font-bold transition-colors ${
              ibanOk ? 'bg-brand-600 text-white hover:bg-brand-700 dark:text-slate-900' : 'bg-surface-2 text-ink-3'
            }`}
          >
            {busy && <span aria-hidden="true" className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
            {t('page.activateButton')}
          </button>
          <p className="text-center text-[10.5px] leading-[1.9] text-ink-3">
            {t('page.activateFootnote')}
          </p>
        </div>
      )}

      {message && (
        <p role="status" className={`rounded-[14px] px-3.5 py-3 text-xs font-semibold leading-[1.85] ${message.good ? 'bg-good-tint text-good-ink' : 'bg-bad-tint text-bad-ink'}`}>
          {message.text}
        </p>
      )}

      {askEnable && <TootiDialog spec={enableAutopayDialog(day, ceiling)} onConfirm={activate} onDismiss={() => setAskEnable(false)} />}
      {askCancel && <TootiDialog spec={cancelAutopayDialog()} onConfirm={cancel} onDismiss={() => setAskCancel(false)} />}
    </div>
  )
}
